import { useEffect, useMemo, useState } from "react";
import { AppShell, PageHeader } from "../components/AppShell";
import {
    Wine, Plus, Search, FileText, X, ChevronRight, Sparkles,
    Trash2, Upload, ShoppingCart, BookOpen, Loader2, Check, Filter, Edit3, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { BEVERAGE_CATEGORIES as BEV_CATEGORIES_DEFAULT } from "../lib/constants";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";

// Mantengo BEV_CATEGORIES come fallback (v23.25 lista espansa). I componenti
// caricano via API le categorie custom utente + standard.
const BEV_CATEGORIES = BEV_CATEGORIES_DEFAULT;

export default function BeverageCost() {
    const [tab, setTab] = useState("beverages");
    return (
        <AppShell active="beverage-cost">
            <PageHeader
                eyebrow="Beverage Cost"
                title="Bevande, cocktail e ordini smart"
                description="Gestisci il listino, esplora 200+ cocktail e genera ordini con il Magic Bartender."
                icon={<Wine size={18} />}
            />
            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <TabBtn active={tab === "beverages"} onClick={() => setTab("beverages")} icon={<Wine size={14} />} label="Bevande" testid="bev-tab-bevs" />
                <TabBtn active={tab === "cocktails"} onClick={() => setTab("cocktails")} icon={<BookOpen size={14} />} label={`Cocktail (200+)`} testid="bev-tab-cocktails" />
                <TabBtn active={tab === "bartender"} onClick={() => setTab("bartender")} icon={<Sparkles size={14} />} label="Magic Bartender" testid="bev-tab-bartender" />
                <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={<ShoppingCart size={14} />} label="Ordini & eventi" testid="bev-tab-orders" />
            </div>

            <div className="mt-5">
                {tab === "beverages" && <BeveragesTab />}
                {tab === "cocktails" && <CocktailsTab />}
                {tab === "bartender" && <MagicBartenderTab />}
                {tab === "orders" && <OrdersTab />}
            </div>
        </AppShell>
    );
}

function TabBtn({ active, onClick, icon, label, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${
                active
                    ? "bg-brand-navy text-white shadow"
                    : "bg-paper border border-brand-line text-brand-ink hover:border-brand-gold/60"
            }`}
        >
            {icon}
            {label}
        </button>
    );
}

// ============================ BEVERAGES TAB ===============================
function BeveragesTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [csvOpen, setCsvOpen] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [bulkBusy, setBulkBusy] = useState(false);
    const [confirmAllOpen, setConfirmAllOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [recatOpen, setRecatOpen] = useState(false);
    const [recatPreview, setRecatPreview] = useState(null);
    const [recatBusy, setRecatBusy] = useState(false);
    const [recatResult, setRecatResult] = useState(null);
    const [recatProgress, setRecatProgress] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set("search", search);
            if (filter) params.set("category", filter);
            const { data } = await api.get(`/beverages?${params.toString()}`);
            setItems(data || []);
            setSelectedIds(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, filter]);

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa bevanda?")) return;
        try { await api.delete(`/beverages/${id}`); load(); toast.success("Bevanda eliminata"); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const selectAll = () => setSelectedIds(new Set(items.map((b) => b.id)));
    const clearSel = () => setSelectedIds(new Set());

    const bulkDelete = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`Eliminare ${selectedIds.size} bevande selezionate? Verranno rimosse anche dagli ingredienti collegati.`)) return;
        setBulkBusy(true);
        try {
            const { data } = await api.post("/beverages/bulk-delete", { ids: Array.from(selectedIds) });
            toast.success(`${data.deleted} bevande eliminate`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    const deleteAll = () => {
        if (items.length === 0) {
            toast.info("Il listino è già vuoto");
            return;
        }
        setConfirmText("");
        setConfirmAllOpen(true);
    };

    const confirmDeleteAll = async () => {
        if (confirmText.trim().toUpperCase() !== "ELIMINA TUTTO") {
            toast.error('Digita esattamente "ELIMINA TUTTO" per confermare');
            return;
        }
        setBulkBusy(true);
        try {
            const { data } = await api.post("/beverages/bulk-delete", { all: true });
            toast.success(`${data.deleted} bevande eliminate (intero listino)`);
            setConfirmAllOpen(false);
            setConfirmText("");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBulkBusy(false); }
    };

    const openRecategorize = async () => {
        setRecatBusy(true);
        setRecatResult(null);
        try {
            const { data } = await api.get("/beverages/recategorize-preview");
            setRecatPreview(data);
            setRecatOpen(true);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecatBusy(false); }
    };

    const runRecategorize = async (forceAll = false) => {
        setRecatBusy(true);
        setRecatResult(null);
        setRecatProgress(null);
        try {
            const { data } = await api.post(
                "/beverages/recategorize",
                { force_all: forceAll },
                { timeout: 120000 },
            );
            // Caso 1: nessuna candidata → backend ritorna direttamente "done"
            if (data.status === "done") {
                setRecatResult(data);
                toast.info("Nessuna bevanda da ricatalogare");
                return;
            }
            // Caso 2: job_id + polling
            const jobId = data.job_id;
            toast.info(`Ricatalogazione avviata · ${data.candidates} bevande in ${data.batches_total} batch. Resta sulla pagina.`);
            const maxTries = 600; // 600 × 3s = 30 min (1 batch ≈ 30-90s)
            let transient = 0;
            for (let i = 0; i < maxTries; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                let status;
                try {
                    const res = await api.get(`/beverages/recategorize-status/${jobId}`);
                    status = res.data;
                    transient = 0;
                } catch (pollErr) {
                    transient++;
                    if (transient >= 8) throw pollErr;
                    continue;
                }
                if (status.progress) setRecatProgress(status.progress);
                if (status.status === "done") {
                    setRecatResult(status);
                    toast.success(`✨ ${status.updated} bevande ricatalogate (${status.credits_spent} crediti spesi)`);
                    load();
                    return;
                }
                if (status.status === "error") {
                    toast.error(status.error || "Errore ricatalogazione");
                    if (status.candidates !== undefined) setRecatResult(status);
                    load();
                    return;
                }
            }
            toast.error("Timeout ricatalogazione (30 min). Ricarica la pagina e riprova con `force_all=false`.");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecatBusy(false); }
    };

    return (
        <div>
            <div className="flex items-center gap-2 flex-wrap mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Cerca bevanda…"
                        data-testid="bev-search"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold"
                    />
                </div>
                <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    data-testid="bev-filter-category"
                    className="px-3 py-2 rounded-lg bg-white border border-brand-line text-sm"
                >
                    <option value="">Tutte le categorie</option>
                    {BEV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                    type="button"
                    onClick={() => { setEditing(null); setDialogOpen(true); }}
                    data-testid="bev-add"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-2 text-sm font-bold hover:bg-brand-navyLight transition shadow"
                >
                    <Plus size={14} /> Aggiungi
                </button>
                <button
                    type="button"
                    onClick={() => setCsvOpen(true)}
                    data-testid="bev-import"
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand-gold/60 bg-brand-gold/10 text-brand-navy px-4 py-2 text-sm font-bold hover:bg-brand-gold/20 transition"
                >
                    <Sparkles size={14} /> Importa con AI
                </button>
                <button
                    type="button"
                    onClick={openRecategorize}
                    disabled={recatBusy || items.length === 0}
                    data-testid="bev-recategorize"
                    title="Ri-cataloga le bevande esistenti con AI (Rhum, Gin, ecc.)"
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400 bg-emerald-50 text-emerald-700 px-4 py-2 text-sm font-bold hover:bg-emerald-100 transition disabled:opacity-50"
                >
                    {recatBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    Ri-cataloga listino
                </button>
            </div>

            {/* Toolbar selezione multipla */}
            {items.length > 0 && (
                <div className="mb-3 flex items-center gap-2 flex-wrap text-xs">
                    <button
                        onClick={selectedIds.size === items.length ? clearSel : selectAll}
                        data-testid="bev-select-all"
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-line bg-white text-brand-ink hover:border-brand-gold font-semibold"
                    >
                        {selectedIds.size === items.length ? "Deseleziona tutto" : "Seleziona tutto"}
                    </button>
                    {selectedIds.size > 0 && (
                        <>
                            <span className="text-brand-muted">{selectedIds.size} selezionate</span>
                            <button
                                onClick={bulkDelete}
                                disabled={bulkBusy}
                                data-testid="bev-bulk-delete"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-brand-danger text-white font-bold hover:bg-red-700 transition disabled:opacity-60"
                            >
                                {bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                                Elimina selezionate
                            </button>
                        </>
                    )}
                    <button
                        onClick={deleteAll}
                        disabled={bulkBusy}
                        data-testid="bev-delete-all"
                        className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-brand-danger/40 text-brand-danger hover:bg-brand-danger/10 font-bold transition"
                    >
                        <Trash2 size={12} /> Elimina TUTTO il listino
                    </button>
                </div>
            )}

            {loading ? (
                <div className="py-10 text-center text-brand-muted"><Loader2 className="mx-auto animate-spin mb-2" /> Carico…</div>
            ) : items.length === 0 ? (
                <div className="py-10 text-center text-brand-muted text-sm">
                    Nessuna bevanda nel listino. Aggiungi la prima o usa "Importa con AI" (CSV, Excel, PDF, foto).
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bev-grid">
                    {items.map((b) => (
                        <BeverageCard
                            key={b.id}
                            bev={b}
                            checked={selectedIds.has(b.id)}
                            onToggle={() => toggleSelect(b.id)}
                            onEdit={() => { setEditing(b); setDialogOpen(true); }}
                            onDelete={() => remove(b.id)}
                        />
                    ))}
                </div>
            )}

            <BeverageDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editing={editing}
                onSaved={load}
            />
            <CsvImportDialog open={csvOpen} onOpenChange={setCsvOpen} onDone={load} />

            {/* Dialog Ri-cataloga listino */}
            <Dialog open={recatOpen} onOpenChange={(v) => { if (!v) { setRecatOpen(false); setRecatResult(null); } }}>
                <DialogContent className="max-w-md bg-white p-0 overflow-hidden" data-testid="bev-recat-dialog">
                    <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-emerald-50 to-paper">
                        <DialogTitle className="font-display text-xl text-brand-ink flex items-center gap-2">
                            <Sparkles size={18} className="text-emerald-600" /> Ri-cataloga listino con AI
                        </DialogTitle>
                        <DialogDescription className="text-sm text-brand-muted">
                            L'AI analizza le bevande con categorie generiche/legacy ("Altro", "Distillato", "Vino", ecc.) e le ricataloga in categorie precise (Rhum, Gin, Vino bianco, Champagne, ecc.). Normalizza anche nomi e marche.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-6 space-y-3">
                        {recatPreview && !recatResult && (
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between border-b border-brand-line pb-2">
                                    <span className="text-brand-muted">Bevande totali nel listino:</span>
                                    <strong className="text-brand-ink">{recatPreview.total_beverages}</strong>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-2">
                                    <span className="text-brand-muted">Da ricatalogare (legacy/Altro):</span>
                                    <strong className="text-emerald-700">{recatPreview.legacy_count}</strong>
                                </div>
                                <div className="flex justify-between border-b border-brand-line pb-2">
                                    <span className="text-brand-muted">Batch AI necessari:</span>
                                    <strong className="text-brand-ink">{recatPreview.ai_batches_needed}</strong>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-brand-muted">Crediti AI richiesti:</span>
                                    <strong className="text-brand-gold">{recatPreview.ai_credits_required} crediti</strong>
                                </div>
                                {recatPreview.legacy_count === 0 && (
                                    <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                                        Tutte le bevande hanno già categorie precise. Puoi comunque forzare la ricatalogazione completa (sconsigliato).
                                    </div>
                                )}
                            </div>
                        )}
                        {recatResult && (
                            <div className="space-y-2 text-sm bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                <div className="text-emerald-700 font-bold flex items-center gap-1.5">
                                    <Check size={14} /> Ricatalogazione completata
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs text-brand-ink">
                                    <div><span className="text-brand-muted">Candidate:</span> <strong>{recatResult.candidates}</strong></div>
                                    <div><span className="text-brand-muted">Aggiornate:</span> <strong className="text-emerald-700">{recatResult.updated}</strong></div>
                                    <div><span className="text-brand-muted">Saltate:</span> <strong>{recatResult.skipped}</strong></div>
                                    <div><span className="text-brand-muted">Batch:</span> <strong>{recatResult.batches}</strong></div>
                                </div>
                                <div className="text-xs text-brand-muted pt-1 border-t border-emerald-200">
                                    Crediti AI spesi: <strong className="text-brand-gold">{recatResult.credits_spent}</strong>
                                </div>
                            </div>
                        )}
                        {recatBusy && recatProgress && !recatResult && (
                            <div className="space-y-2 text-sm bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="bev-recat-progress">
                                <div className="text-blue-700 font-bold flex items-center gap-1.5">
                                    <Loader2 size={14} className="animate-spin" /> Ricatalogazione in corso…
                                </div>
                                <div className="text-xs text-brand-ink">
                                    Batch <strong>{recatProgress.batches_done}</strong> di <strong>{recatProgress.batches_total}</strong>
                                    {recatProgress.updated_so_far !== undefined && <> · {recatProgress.updated_so_far} bevande aggiornate finora</>}
                                </div>
                                <div className="w-full bg-blue-100 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className="bg-blue-500 h-1.5 transition-all"
                                        style={{ width: `${Math.round((recatProgress.batches_done / Math.max(1, recatProgress.batches_total)) * 100)}%` }}
                                    />
                                </div>
                                <div className="text-[11px] text-brand-muted">
                                    Il processo continua anche se chiudi questo dialog. Ogni batch dura 30-90 secondi.
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2 flex-wrap">
                        <button
                            onClick={() => { setRecatOpen(false); setRecatResult(null); }}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                        >
                            {recatResult ? "Chiudi" : "Annulla"}
                        </button>
                        {!recatResult && recatPreview && recatPreview.legacy_count > 0 && (
                            <button
                                onClick={() => runRecategorize(false)}
                                disabled={recatBusy}
                                data-testid="bev-recat-run"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition shadow disabled:opacity-50"
                            >
                                {recatBusy
                                    ? <><Loader2 size={13} className="animate-spin" /> Ricatalogo {recatPreview.legacy_count} bevande…</>
                                    : <><Sparkles size={13} /> Ricataloga {recatPreview.legacy_count} bevande · {recatPreview.ai_credits_required} crediti</>}
                            </button>
                        )}
                        {!recatResult && recatPreview && recatPreview.legacy_count === 0 && (
                            <button
                                onClick={() => runRecategorize(true)}
                                disabled={recatBusy}
                                data-testid="bev-recat-force"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 transition shadow disabled:opacity-50"
                            >
                                {recatBusy
                                    ? <><Loader2 size={13} className="animate-spin" /> Ricatalogo…</>
                                    : <><Sparkles size={13} /> Forza ricatalogazione completa</>}
                            </button>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Dialog conferma elimina tutto */}
            <Dialog open={confirmAllOpen} onOpenChange={(v) => { if (!v) { setConfirmAllOpen(false); setConfirmText(""); } }}>
                <DialogContent className="max-w-md bg-white p-0 overflow-hidden" data-testid="bev-confirm-delete-all">
                    <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-red-50 to-paper">
                        <DialogTitle className="font-display text-xl text-brand-ink flex items-center gap-2">
                            <Trash2 size={18} className="text-brand-danger" /> Elimina TUTTO il listino
                        </DialogTitle>
                        <DialogDescription className="text-sm text-brand-muted">
                            Stai per eliminare <strong>{items.length} bevande</strong> dal listino e tutti gli ingredienti food cost collegati. L'azione è <strong>irreversibile</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="p-6 space-y-3">
                        <div className="text-sm text-brand-ink">
                            Per confermare, digita <code className="bg-brand-cream px-2 py-0.5 rounded font-bold text-brand-danger">ELIMINA TUTTO</code> qui sotto:
                        </div>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            placeholder="ELIMINA TUTTO"
                            data-testid="bev-confirm-text"
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && confirmText.trim().toUpperCase() === "ELIMINA TUTTO" && !bulkBusy) {
                                    confirmDeleteAll();
                                }
                            }}
                            className="w-full px-4 py-2.5 rounded-lg border-2 border-brand-line focus:border-brand-danger bg-white text-sm font-mono uppercase outline-none"
                        />
                    </div>
                    <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2">
                        <button
                            onClick={() => { setConfirmAllOpen(false); setConfirmText(""); }}
                            className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                        >
                            Annulla
                        </button>
                        <button
                            onClick={confirmDeleteAll}
                            disabled={bulkBusy || confirmText.trim().toUpperCase() !== "ELIMINA TUTTO"}
                            data-testid="bev-confirm-delete-all-btn"
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-danger text-white text-sm font-bold hover:bg-red-700 transition shadow disabled:opacity-50"
                        >
                            {bulkBusy ? <><Loader2 size={13} className="animate-spin" /> Elimino…</> : <><Trash2 size={13} /> Elimina tutto</>}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function BeverageCard({ bev, checked, onToggle, onEdit, onDelete }) {
    const pricePerMl = bev.volume_ml > 0 ? (bev.price_eur / bev.volume_ml) : 0;
    return (
        <div className={`bg-paper border rounded-xl p-4 transition ${checked ? "border-brand-gold ring-2 ring-brand-gold/30" : "border-brand-line hover:border-brand-gold/60"}`} data-testid={`bev-card-${bev.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={onToggle}
                        data-testid={`bev-check-${bev.id}`}
                        className="mt-1 h-4 w-4 accent-brand-gold cursor-pointer"
                    />
                    <div className="min-w-0">
                        <div className="font-bold text-brand-ink truncate">{bev.name}</div>
                        {bev.brand && <div className="text-[11px] text-brand-muted truncate">{bev.brand}</div>}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="font-bold text-brand-navy">€ {Number(bev.price_eur || 0).toFixed(2)}</div>
                    <div className="text-[10px] text-brand-muted">{bev.volume_ml || 0} ml · {bev.unit || "bottiglia"}</div>
                </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[11px] mb-2">
                <span className="px-2 py-0.5 rounded-full bg-brand-cream/60 text-brand-ink border border-brand-line">{bev.category}</span>
                {bev.abv > 0 && <span className="text-brand-muted">{bev.abv}° vol</span>}
                {pricePerMl > 0 && <span className="text-brand-muted">{(pricePerMl * 100).toFixed(2)} €/100ml</span>}
            </div>
            {bev.supplier_name && <div className="text-[11px] text-brand-muted truncate">Fornitore: {bev.supplier_name}</div>}
            <div className="mt-3 flex items-center gap-2">
                <button onClick={onEdit} className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition" data-testid={`bev-edit-${bev.id}`}>Modifica</button>
                <button onClick={onDelete} className="text-[11px] font-semibold text-brand-danger/80 hover:text-brand-danger transition ml-auto" data-testid={`bev-delete-${bev.id}`}>
                    <Trash2 size={11} className="inline" /> Elimina
                </button>
            </div>
        </div>
    );
}

function BeverageDialog({ open, onOpenChange, editing, onSaved }) {
    const empty = { name: "", category: "Distillato", brand: "", volume_ml: 700, abv: 0, price_eur: 0, supplier_name: "", unit: "bottiglia" };
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => { setForm(editing ? { ...empty, ...editing } : empty); /* eslint-disable-next-line */ }, [editing, open]);

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Inserisci il nome"); return; }
        setSaving(true);
        try {
            const payload = { ...form, volume_ml: Number(form.volume_ml) || 0, abv: Number(form.abv) || 0, price_eur: Number(form.price_eur) || 0 };
            if (editing) await api.put(`/beverages/${editing.id}`, payload);
            else await api.post(`/beverages`, payload);
            toast.success(editing ? "Bevanda aggiornata" : "Bevanda aggiunta");
            onSaved?.();
            onOpenChange(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl bg-white p-0 overflow-hidden" data-testid="bev-dialog">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <DialogTitle className="font-display text-xl text-brand-ink">
                        {editing ? "Modifica bevanda" : "Nuova bevanda"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        Inserisci nome, categoria, formato e prezzo: ti servirà per il calcolo del Magic Bartender.
                    </DialogDescription>
                </DialogHeader>
                <div className="p-6 space-y-3">
                    <Field label="Nome *">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="bev-form-name" className="inp" placeholder="Es. Gin Bombay Sapphire" />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Categoria">
                            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" data-testid="bev-form-category">
                                {BEV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </Field>
                        <Field label="Brand"><input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="inp" /></Field>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Volume (ml)"><input type="number" value={form.volume_ml} onChange={(e) => setForm({ ...form, volume_ml: e.target.value })} className="inp" data-testid="bev-form-volume" /></Field>
                        <Field label="Gradazione"><input type="number" step="0.5" value={form.abv} onChange={(e) => setForm({ ...form, abv: e.target.value })} className="inp" /></Field>
                        <Field label="Prezzo €"><input type="number" step="0.10" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} className="inp" data-testid="bev-form-price" /></Field>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Fornitore"><input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="inp" /></Field>
                        <Field label="Formato">
                            <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="inp">
                                <option>bottiglia</option><option>lattina</option><option>brick</option><option>cartone</option>
                            </select>
                        </Field>
                    </div>
                </div>
                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2">
                    <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Annulla</button>
                    <button onClick={submit} disabled={saving} data-testid="bev-form-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60">
                        {saving ? <><Loader2 size={13} className="animate-spin" /> Salvo…</> : <><Check size={13} /> Salva</>}
                    </button>
                </div>
            </DialogContent>
            <style>{`.inp{width:100%;padding:.5rem .75rem;border-radius:.5rem;background:#fff;border:1px solid #e6dfd2;font-size:13px;outline:none}.inp:focus{border-color:#c9a961}`}</style>
        </Dialog>
    );
}

function CsvImportDialog({ open, onOpenChange, onDone }) {
    // Step: "upload" | "mapping" | "preview" | "saving"
    const [step, setStep] = useState("upload");
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null);
    const [supplier, setSupplier] = useState({ name: "", city: "", region: "", email: "", phone: "", vat: "", website: "" });
    const [rows, setRows] = useState([]);
    const [source, setSource] = useState("");
    const [supplierExistingId, setSupplierExistingId] = useState(null);
    const [rawHeaders, setRawHeaders] = useState([]);
    const [rawRows, setRawRows] = useState([]);
    const [mapping, setMapping] = useState({}); // {name: header, category: header, ...}
    const [autoAiCategorize, setAutoAiCategorize] = useState(true); // toggle "Cataloga con AI"
    const [aiCategorizing, setAiCategorizing] = useState(false);
    const [aiCategorizedCount, setAiCategorizedCount] = useState(0);

    const reset = () => {
        setStep("upload"); setFile(null); setBusy(false); setProgress(null);
        setSupplier({ name: "", city: "", region: "", email: "", phone: "", vat: "", website: "" });
        setRows([]); setSource(""); setSupplierExistingId(null);
        setRawHeaders([]); setRawRows([]); setMapping({});
        setAutoAiCategorize(true); setAiCategorizing(false); setAiCategorizedCount(0);
    };
    const close = () => { reset(); onOpenChange(false); };

    // Auto-detect mapping iniziale basata sui nomi delle colonne (case-insensitive)
    const autoMapping = (headers) => {
        const m = { name: "", category: "", brand: "", volume_ml: "", abv: "", price_eur: "", unit: "" };
        const lc = headers.map((h) => (h || "").toString().trim().toLowerCase());
        const guess = (...alts) => {
            for (const a of alts) {
                const idx = lc.findIndex((h) => h === a || h.includes(a));
                if (idx >= 0) return headers[idx];
            }
            return "";
        };
        m.name = guess("name", "nome", "prodotto", "denominazione", "descrizione");
        m.category = guess("category", "categoria", "tipologia", "marchio", "tipo");
        m.brand = guess("brand", "marca", "produttore");
        m.volume_ml = guess("volume_ml", "ml", "volume", "formato_ml", "cl");
        m.abv = guess("abv", "gradazione", "alcol", "gradi");
        m.price_eur = guess("price_eur", "prezzo", "prezzo_euro", "prezzo_unitario", "costo");
        m.unit = guess("unit", "formato", "confezione", "unita");
        return m;
    };

    // Ricostruisce le righe `beverages` applicando la mappatura corrente
    const applyMapping = (rawRowsArr, headersArr, map) => {
        const toFloat = (v) => {
            if (v === null || v === undefined || v === "") return 0;
            try {
                let s = String(v).replace("€", "").replace(/\s/g, "").trim();
                // gestisci 1.234,56 (it) vs 1,234.56 (en)
                if (s.includes(",") && s.includes(".")) {
                    s = s.replace(/\./g, "").replace(",", ".");
                } else if (s.includes(",")) {
                    s = s.replace(",", ".");
                }
                return parseFloat(s) || 0;
            } catch { return 0; }
        };
        const lcHeaders = headersArr.map((h) => (h || "").toString().trim().toLowerCase());
        const get = (row, header) => {
            if (!header) return "";
            // raw_rows usano chiavi lowercase normalizzate dal backend
            const k = (header || "").toString().trim().toLowerCase();
            if (k in row) return row[k];
            // Fallback: cerca per indice
            const idx = lcHeaders.indexOf(k);
            if (idx >= 0 && headersArr[idx]) {
                const orig = headersArr[idx];
                return row[orig] ?? row[orig.toLowerCase()] ?? "";
            }
            return "";
        };
        const out = [];
        let i = 0;
        for (const r of rawRowsArr) {
            const nm = String(get(r, map.name) || "").trim();
            if (!nm) continue;
            out.push({
                _rowId: i++,
                name: nm,
                category: String(get(r, map.category) || "Altro").trim() || "Altro",
                brand: String(get(r, map.brand) || "").trim(),
                volume_ml: toFloat(get(r, map.volume_ml)),
                abv: toFloat(get(r, map.abv)),
                price_eur: toFloat(get(r, map.price_eur)),
                unit: String(get(r, map.unit) || "bottiglia").trim() || "bottiglia",
            });
        }
        return out;
    };

    const parse = async () => {
        if (!file) return;
        // Pre-check dimensione
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > 100) {
            toast.error(`Il file pesa ${sizeMB.toFixed(1)} MB (max 100 MB). Comprimi il PDF o caricalo in più parti.`);
            return;
        }
        if (sizeMB > 30) {
            toast.warning(`File da ${sizeMB.toFixed(1)} MB: upload e analisi possono richiedere 5-15 minuti.`, { duration: 8000 });
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(`/beverages/import/ai-parse`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000, // 10 min upload window per file molto grandi
            });

            // AI async: poll until done
            let result = data;
            if (data.source === "ai" && data.job_id && data.status === "pending") {
                toast.info("AI sta analizzando il documento. PDF lunghi possono richiedere fino a 20 minuti.");
                const jobId = data.job_id;
                const maxTries = 480;
                let transientErrors = 0;
                for (let i = 0; i < maxTries; i++) {
                    await new Promise((res) => setTimeout(res, 3000));
                    let status;
                    try {
                        const r = await api.get(`/beverages/import/ai-status/${jobId}`);
                        status = r.data;
                        transientErrors = 0;
                    } catch (pollErr) {
                        transientErrors++;
                        if (transientErrors >= 8) throw pollErr;
                        continue;
                    }
                    if (status.progress) setProgress(status.progress);
                    if (status.status === "done") { result = status; break; }
                    if (status.status === "error") {
                        toast.error(status.error || "Errore AI");
                        setBusy(false);
                        return;
                    }
                }
                if (result.status === "pending" || result.status === "running") {
                    toast.error("Analisi AI ancora in corso. Riprova fra qualche istante con un file più piccolo o convertilo in immagine JPG.");
                    setBusy(false);
                    return;
                }
            }

            setSource(result.source || "");
            setSupplier({
                name: result.supplier?.name || "",
                city: result.supplier?.city || "",
                region: result.supplier?.region || "",
                email: result.supplier?.email || "",
                phone: result.supplier?.phone || "",
                vat: result.supplier?.vat || "",
                website: result.supplier?.website || "",
            });
            setSupplierExistingId(result.supplier_existing_id || null);

            const isTabular = result.source === "csv" || result.source === "excel";
            if (isTabular && result.raw_headers && result.raw_headers.length > 0) {
                // Mostra step di mapping colonne
                setRawHeaders(result.raw_headers);
                setRawRows(result.raw_rows || []);
                const auto = autoMapping(result.raw_headers);
                setMapping(auto);
                // Pre-popola le righe con il mapping auto-rilevato (backend già lo fa)
                let initialRows;
                if (result.beverages && result.beverages.length > 0) {
                    initialRows = result.beverages.map((b, i) => ({ ...b, _rowId: i }));
                } else {
                    initialRows = applyMapping(result.raw_rows || [], result.raw_headers, auto);
                }
                setRows(initialRows);
                setStep("mapping");
                toast.success(`File rilevato: ${result.raw_headers.length} colonne · ${initialRows.length} righe.`);

                // v23.27: se toggle AI ON, cataloga AUTOMATICAMENTE già adesso
                // così la maschera di mapping mostra subito le categorie corrette
                if (autoAiCategorize && initialRows.length > 0 && initialRows.length <= 200) {
                    const valid = initialRows.filter((r) => r.name && r.name.trim());
                    if (valid.length > 0) {
                        setAiCategorizing(true);
                        try {
                            const aiPayload = {
                                rows: valid.map((r) => ({
                                    name: r.name,
                                    brand: r.brand || "",
                                    abv: Number(r.abv) || 0,
                                    volume_ml: Number(r.volume_ml) || 0,
                                    category: r.category || "",
                                })),
                            };
                            const { data: aiData } = await api.post(
                                "/beverages/import/ai-categorize", aiPayload, { timeout: 120000 },
                            );
                            const aiRows = aiData.rows || [];
                            let aiIdx = 0;
                            const newRows = initialRows.map((r) => {
                                if (!r.name || !r.name.trim()) return r;
                                const a = aiRows[aiIdx++];
                                if (!a) return r;
                                return {
                                    ...r,
                                    name: a.name || r.name,
                                    brand: a.brand || r.brand,
                                    category: a.category || r.category,
                                };
                            });
                            setRows(newRows);
                            setAiCategorizedCount(aiData.categorized || aiRows.length);
                            toast.success(`✨ ${aiData.categorized || aiRows.length} bevande catalogate con AI (1 credito speso)`);
                        } catch (aiErr) {
                            toast.warning(`Catalogazione AI non riuscita (${formatApiError(aiErr)}). Le categorie restano come da file.`);
                        } finally {
                            setAiCategorizing(false);
                        }
                    }
                }
            } else {
                if (!result.beverages || result.beverages.length === 0) {
                    toast.error("Nessuna bevanda rilevata nel file. Verifica che le colonne o l'immagine siano leggibili.");
                    setBusy(false);
                    return;
                }
                setRows(result.beverages.map((b, i) => ({ ...b, _rowId: i })));
                setStep("preview");
                const friendly = result.source === "ai" ? "AI Vision" : (result.source === "excel" ? "Excel" : "CSV");
                const pagesInfo = result.pages_count && result.pages_count > 1
                    ? ` · ${result.pages_count} pagine analizzate${result.pages_errors ? ` (${result.pages_errors} con errori)` : ""}`
                    : "";
                toast.success(`${result.beverages.length} bevande rilevate (${friendly})${pagesInfo}. Modifica e conferma per salvare.`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // Quando l'utente cambia la mappatura, ricostruisce le righe
    const updateMappingField = (field, header) => {
        const newMap = { ...mapping, [field]: header };
        setMapping(newMap);
        setRows(applyMapping(rawRows, rawHeaders, newMap));
    };

    // Cataloga con AI le righe correnti (batch ≤ 200, 1 credito)
    const runAiCategorize = async () => {
        const candidates = rows.filter((r) => r.name && r.name.trim());
        if (candidates.length === 0) {
            toast.error("Nessuna riga valida da catalogare");
            return;
        }
        if (candidates.length > 200) {
            toast.error(`Troppe righe per la catalogazione AI (${candidates.length}, max 200). Divisi l'import in più parti.`);
            return;
        }
        setAiCategorizing(true);
        try {
            const payload = {
                rows: candidates.map((r) => ({
                    name: r.name,
                    brand: r.brand || "",
                    abv: Number(r.abv) || 0,
                    volume_ml: Number(r.volume_ml) || 0,
                    category: r.category || "",
                })),
            };
            const { data } = await api.post("/beverages/import/ai-categorize", payload, { timeout: 120000 });
            const aiRows = data.rows || [];
            // Re-apply: stesso ordine dei candidates → mappiamo dentro `rows`
            let aiIdx = 0;
            const newRows = rows.map((r) => {
                if (!r.name || !r.name.trim()) return r;
                const a = aiRows[aiIdx++];
                if (!a) return r;
                return {
                    ...r,
                    name: a.name || r.name,
                    brand: a.brand || r.brand,
                    category: a.category || r.category,
                };
            });
            setRows(newRows);
            setAiCategorizedCount(data.categorized || aiRows.length);
            toast.success(`✨ ${data.categorized || aiRows.length} righe catalogate con AI`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setAiCategorizing(false);
        }
    };

    const save = async () => {
        if (!supplier.name.trim()) {
            toast.error("Inserisci il nome del fornitore");
            return;
        }
        const validRows = rows.filter((r) => r.name && r.name.trim());
        if (validRows.length === 0) {
            toast.error("Nessuna riga valida da salvare");
            return;
        }
        setBusy(true); setStep("saving");
        try {
            const payload = {
                supplier: {
                    name: supplier.name.trim(),
                    city: supplier.city.trim(),
                    region: supplier.region.trim(),
                    email: supplier.email.trim(),
                    phone: supplier.phone.trim(),
                    vat: supplier.vat.trim(),
                    website: supplier.website.trim(),
                },
                beverages: validRows.map(({ _rowId, ...b }) => ({
                    name: b.name.trim(),
                    category: b.category || "Altro",
                    brand: (b.brand || "").trim(),
                    volume_ml: Number(b.volume_ml) || 0,
                    abv: Number(b.abv) || 0,
                    price_eur: Number(b.price_eur) || 0,
                    unit: (b.unit || "bottiglia").trim() || "bottiglia",
                })),
            };
            const { data } = await api.post(`/beverages/import/ai-confirm`, payload);
            toast.success(`${data.created} bevande salvate · Fornitore: ${data.supplier.name}`);
            onDone?.();
            close();
        } catch (e) { toast.error(formatApiError(e)); setStep("preview"); }
        finally { setBusy(false); }
    };

    const updateRow = (rowId, field, value) => {
        setRows((rs) => rs.map((r) => r._rowId === rowId ? { ...r, [field]: value } : r));
    };
    const removeRow = (rowId) => setRows((rs) => rs.filter((r) => r._rowId !== rowId));
    const addRow = () => setRows((rs) => [...rs, {
        _rowId: Date.now(), name: "", category: "Altro", brand: "", volume_ml: 0, abv: 0, price_eur: 0, unit: "bottiglia",
    }]);

    return (
        <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
            <DialogContent className={`bg-white p-0 overflow-hidden ${step === "preview" || step === "mapping" ? "max-w-5xl" : "max-w-md"}`} data-testid="bev-csv-dialog">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <DialogTitle className="font-display text-xl text-brand-ink flex items-center gap-2">
                        <Sparkles size={18} className="text-brand-gold" />
                        {step === "upload" && "Importa listino bevande"}
                        {step === "mapping" && "Mappa le colonne del file"}
                        {step === "preview" && "Verifica e modifica il listino"}
                        {step === "saving" && "Salvataggio in corso…"}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        {step === "upload" && "Carica qualsiasi formato: CSV, Excel, PDF o foto. L'AI riconoscerà fornitore e bevande automaticamente."}
                        {step === "mapping" && "Per ogni campo del sistema, scegli quale colonna del tuo file vuoi usare. Es. la colonna \"marchio\" può essere mappata su Categoria."}
                        {step === "preview" && (source === "ai" ? "AI ha analizzato il documento. Controlla i dati, modifica o elimina prima di salvare." : "Controlla i dati estratti e completa il fornitore prima di salvare.")}
                    </DialogDescription>
                </DialogHeader>

                {step === "upload" && (
                    <div className="p-6 space-y-4">
                        <label className="block border-2 border-dashed border-brand-gold/40 bg-brand-gold/5 rounded-xl p-6 text-center cursor-pointer hover:bg-brand-gold/10 transition" data-testid="bev-ai-dropzone">
                            <Upload size={28} className="mx-auto text-brand-gold mb-2" />
                            <div className="text-sm font-semibold text-brand-ink mb-1">
                                {file ? file.name : "Trascina o seleziona un file"}
                            </div>
                            {file && (
                                <div className={`text-[11px] mb-1 ${file.size / (1024*1024) > 30 ? "text-amber-700 font-bold" : "text-brand-muted"}`}>
                                    {(file.size / (1024 * 1024)).toFixed(1)} MB
                                    {file.size / (1024*1024) > 30 ? " · file grande, analisi 5-15 min" : ""}
                                </div>
                            )}
                            <div className="text-[11px] text-brand-muted">
                                CSV · Excel (.xlsx, .xls) · PDF · JPG · PNG · WEBP — max 100 MB
                            </div>
                            <input
                                type="file"
                                accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                onChange={(e) => setFile(e.target.files?.[0])}
                                data-testid="bev-csv-file"
                                className="hidden"
                            />
                        </label>
                        <div className="text-[11px] text-brand-muted bg-brand-cream rounded-lg p-3 leading-snug">
                            <strong className="text-brand-ink">CSV/Excel</strong>: gratis, colonne attese <code>name, category, brand, volume_ml, abv, price_eur, unit</code>.<br />
                            <strong className="text-brand-ink">PDF/foto</strong>: analisi AI · <span className="text-brand-gold font-bold">1 credito per pagina</span> · max <strong>2 pagine</strong> a volta (splitta PDF grandi).
                        </div>

                        {/* Toggle Cataloga con AI */}
                        <label className="flex items-start gap-2 p-3 bg-brand-gold/5 border border-brand-gold/40 rounded-xl cursor-pointer hover:bg-brand-gold/10 transition" data-testid="bev-ai-cat-toggle">
                            <input
                                type="checkbox"
                                checked={autoAiCategorize}
                                onChange={(e) => setAutoAiCategorize(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-brand-gold cursor-pointer"
                            />
                            <div className="flex-1">
                                <div className="text-sm font-bold text-brand-ink flex items-center gap-1.5">
                                    <Sparkles size={13} className="text-brand-gold" />
                                    Cataloga automaticamente con AI <span className="text-[10px] uppercase font-bold bg-brand-gold/30 text-brand-navy px-1.5 py-0.5 rounded">consigliato</span>
                                </div>
                                <div className="text-[11px] text-brand-muted mt-0.5">
                                    L'AI assegnerà categorie precise (Rhum, Gin, Vino bianco, Champagne, ecc.) basandosi sui nomi/marchi, così non perdi nessuna bevanda nelle ricerche. <span className="text-brand-gold font-bold">Costa 1 credito AI</span>. Funziona per CSV/Excel — i PDF/foto sono già catalogati durante l'OCR.
                                </div>
                            </div>
                        </label>
                        {busy && progress && progress.total > 1 && (
                            <div className="bg-brand-navy/5 border border-brand-navy/20 rounded-xl p-4 space-y-2" data-testid="bev-ai-progress">
                                <div className="flex justify-between text-xs font-bold text-brand-ink">
                                    <span>Pagina {progress.current} di {progress.total}</span>
                                    <span className="text-brand-gold">{progress.found} bevande trovate</span>
                                </div>
                                <div className="h-2 bg-brand-line rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-brand-gold transition-all duration-500"
                                        style={{ width: `${Math.round((progress.current / progress.total) * 100)}%` }}
                                    />
                                </div>
                                <div className="text-[10px] text-brand-muted text-center">
                                    Stiamo analizzando ogni pagina · circa 30s per pagina
                                </div>
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={close} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Annulla</button>
                            <button
                                onClick={parse}
                                disabled={!file || busy}
                                data-testid="bev-csv-upload"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60"
                            >
                                {busy ? <><Loader2 size={14} className="animate-spin" /> Analizzo… (PDF lunghi: 3-5 min)</> : <><Sparkles size={14} /> Analizza</>}
                            </button>
                        </div>
                    </div>
                )}

                {step === "mapping" && (
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4" data-testid="bev-mapping">
                        <div className="bg-brand-cream/60 border border-brand-line rounded-xl p-4">
                            <div className="text-xs uppercase tracking-wider font-bold text-brand-navy mb-2">Mappatura colonne</div>
                            <div className="text-[11px] text-brand-muted mb-3">
                                Il file ha <strong>{rawHeaders.length}</strong> colonne e <strong>{rawRows.length}</strong> righe. Scegli quale colonna usare per ogni campo del sistema.
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {[
                                    { key: "name", label: "Nome bevanda *" },
                                    { key: "category", label: "Categoria" },
                                    { key: "brand", label: "Marca / Brand" },
                                    { key: "volume_ml", label: "Volume (ml)" },
                                    { key: "abv", label: "Gradazione (ABV)" },
                                    { key: "price_eur", label: "Prezzo (€)" },
                                    { key: "unit", label: "Formato / Unità" },
                                ].map((f) => (
                                    <div key={f.key}>
                                        <div className="text-[10px] uppercase tracking-wider font-semibold text-brand-muted mb-1">{f.label}</div>
                                        <select
                                            value={mapping[f.key] || ""}
                                            onChange={(e) => updateMappingField(f.key, e.target.value)}
                                            data-testid={`bev-map-${f.key}`}
                                            className="w-full px-2 py-1.5 rounded border border-brand-line bg-white text-xs"
                                        >
                                            <option value="">— Ignora —</option>
                                            {rawHeaders.map((h, i) => (
                                                <option key={i} value={h}>{h || `(colonna ${i+1})`}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Anteprima righe dopo mapping */}
                        <div>
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-navy">
                                    Anteprima · <span className="text-brand-gold">{rows.length}</span> righe valide
                                    {aiCategorizedCount > 0 && (
                                        <span className="ml-2 text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                                            <Sparkles size={9} className="inline" /> Catalogate con AI
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={runAiCategorize}
                                    disabled={aiCategorizing || rows.length === 0 || rows.length > 200}
                                    data-testid="bev-mapping-ai-categorize"
                                    className="inline-flex items-center gap-1 text-[11px] font-bold bg-brand-gold/15 hover:bg-brand-gold/30 text-brand-navy px-3 py-1.5 rounded-full transition border border-brand-gold/40 disabled:opacity-50"
                                >
                                    {aiCategorizing
                                        ? <><Loader2 size={11} className="animate-spin" /> Cataloghiamo…</>
                                        : <><Sparkles size={11} /> {aiCategorizedCount > 0 ? "Ri-cataloga" : "Cataloga"} con AI · 1 credito</>}
                                </button>
                            </div>
                            {rows.length === 0 ? (
                                <div className="text-sm text-amber-700 bg-amber-50 border border-amber-300 rounded-lg p-3">
                                    Nessuna riga valida con la mappatura corrente. Assicurati di aver mappato almeno il campo <strong>Nome bevanda</strong>.
                                </div>
                            ) : (
                                <div className="overflow-x-auto border border-brand-line rounded-lg max-h-[300px] overflow-y-auto">
                                    <table className="w-full text-xs">
                                        <thead className="bg-brand-cream sticky top-0">
                                            <tr className="text-left">
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Nome</th>
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Categoria</th>
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Brand</th>
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Vol(ml)</th>
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">ABV</th>
                                                <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">€</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {rows.slice(0, 100).map((r) => (
                                                <tr key={r._rowId} className="border-t border-brand-line">
                                                    <td className="px-2 py-1 text-brand-ink">{r.name}</td>
                                                    <td className="px-2 py-1 text-brand-muted">{r.category}</td>
                                                    <td className="px-2 py-1 text-brand-muted">{r.brand}</td>
                                                    <td className="px-2 py-1 text-brand-muted">{r.volume_ml}</td>
                                                    <td className="px-2 py-1 text-brand-muted">{r.abv}</td>
                                                    <td className="px-2 py-1 text-brand-muted">€ {Number(r.price_eur || 0).toFixed(2)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {rows.length > 100 && (
                                        <div className="bg-brand-cream/40 border-t border-brand-line text-[10px] text-brand-muted text-center py-1">
                                            …e altre {rows.length - 100} righe non mostrate
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {step === "preview" && (
                    <div className="px-6 py-4 max-h-[70vh] overflow-y-auto space-y-4" data-testid="bev-ai-preview">
                        {/* Supplier section */}
                        <div className="bg-brand-cream rounded-xl p-4 border border-brand-line">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-navy">Fornitore</div>
                                {supplierExistingId && (
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                        <Check size={10} className="inline mr-1" /> Già nel tuo portale
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                <input value={supplier.name} onChange={(e) => setSupplier({ ...supplier, name: e.target.value })} placeholder="Nome fornitore *" data-testid="bev-ai-supplier-name" className="inp" />
                                <input value={supplier.city} onChange={(e) => setSupplier({ ...supplier, city: e.target.value })} placeholder="Città" className="inp" />
                                <input value={supplier.email} onChange={(e) => setSupplier({ ...supplier, email: e.target.value })} placeholder="Email ordini" className="inp" />
                                <input value={supplier.phone} onChange={(e) => setSupplier({ ...supplier, phone: e.target.value })} placeholder="Telefono" className="inp" />
                                <input value={supplier.vat} onChange={(e) => setSupplier({ ...supplier, vat: e.target.value })} placeholder="P.IVA" className="inp" />
                                <input value={supplier.region} onChange={(e) => setSupplier({ ...supplier, region: e.target.value })} placeholder="Regione" className="inp" />
                            </div>
                        </div>

                        {/* Beverages table */}
                        <div>
                            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                                <div className="text-xs uppercase tracking-wider font-bold text-brand-navy">
                                    Bevande rilevate · <span className="text-brand-gold">{rows.length}</span>
                                    {aiCategorizedCount > 0 && (
                                        <span className="ml-2 text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">
                                            <Sparkles size={9} className="inline" /> Catalogate con AI
                                        </span>
                                    )}
                                </div>
                                <div className="flex items-center gap-2">
                                    {source !== "ai" && (
                                        <button
                                            onClick={runAiCategorize}
                                            disabled={aiCategorizing || rows.length === 0 || rows.length > 200}
                                            data-testid="bev-ai-categorize-manual"
                                            className="inline-flex items-center gap-1 text-[11px] font-bold bg-brand-gold/15 hover:bg-brand-gold/30 text-brand-navy px-3 py-1.5 rounded-full transition border border-brand-gold/40 disabled:opacity-50"
                                        >
                                            {aiCategorizing
                                                ? <><Loader2 size={11} className="animate-spin" /> Cataloghiamo…</>
                                                : <><Sparkles size={11} /> {aiCategorizedCount > 0 ? "Ri-cataloga" : "Cataloga"} con AI · 1 credito</>}
                                        </button>
                                    )}
                                    <button onClick={addRow} className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition inline-flex items-center gap-1">
                                        <Plus size={11} /> Aggiungi riga
                                    </button>
                                </div>
                            </div>
                            <div className="overflow-x-auto border border-brand-line rounded-lg">
                                <table className="w-full text-xs">
                                    <thead className="bg-brand-cream sticky top-0">
                                        <tr className="text-left">
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Nome</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Categoria</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Marca</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Vol(ml)</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">ABV</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">€</th>
                                            <th className="px-2 py-2 font-bold text-brand-muted uppercase text-[10px] tracking-wider">Unità</th>
                                            <th className="w-8"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r._rowId} className="border-t border-brand-line hover:bg-brand-cream/40">
                                                <td className="px-1 py-1"><input value={r.name} onChange={(e) => updateRow(r._rowId, "name", e.target.value)} className="inp !py-1 !text-xs" /></td>
                                                <td className="px-1 py-1">
                                                    <select value={r.category} onChange={(e) => updateRow(r._rowId, "category", e.target.value)} className="inp !py-1 !text-xs">
                                                        {BEV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                                    </select>
                                                </td>
                                                <td className="px-1 py-1"><input value={r.brand || ""} onChange={(e) => updateRow(r._rowId, "brand", e.target.value)} className="inp !py-1 !text-xs" /></td>
                                                <td className="px-1 py-1"><input type="number" value={r.volume_ml} onChange={(e) => updateRow(r._rowId, "volume_ml", e.target.value)} className="inp !py-1 !text-xs w-20" /></td>
                                                <td className="px-1 py-1"><input type="number" step="0.1" value={r.abv} onChange={(e) => updateRow(r._rowId, "abv", e.target.value)} className="inp !py-1 !text-xs w-16" /></td>
                                                <td className="px-1 py-1"><input type="number" step="0.01" value={r.price_eur} onChange={(e) => updateRow(r._rowId, "price_eur", e.target.value)} className="inp !py-1 !text-xs w-20" /></td>
                                                <td className="px-1 py-1"><input value={r.unit || ""} onChange={(e) => updateRow(r._rowId, "unit", e.target.value)} className="inp !py-1 !text-xs w-20" /></td>
                                                <td className="px-1 py-1 text-center"><button onClick={() => removeRow(r._rowId)} className="text-brand-danger/70 hover:text-brand-danger"><Trash2 size={12} /></button></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {step === "saving" && (
                    <div className="p-10 text-center">
                        <Loader2 className="mx-auto animate-spin text-brand-gold mb-3" size={32} />
                        <div className="text-sm text-brand-muted">Sto salvando il fornitore e le bevande…</div>
                    </div>
                )}

                {step !== "saving" && (
                    <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2">
                        {step === "preview" && (
                            <button onClick={() => setStep("upload")} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">← Cambia file</button>
                        )}
                        {step === "mapping" && (
                            <button onClick={() => setStep("upload")} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">← Cambia file</button>
                        )}
                        <button onClick={close} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Annulla</button>
                        {step === "mapping" && (
                            <button
                                onClick={() => setStep("preview")}
                                disabled={rows.length === 0 || aiCategorizing}
                                data-testid="bev-mapping-next"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60"
                            >
                                {aiCategorizing
                                    ? <><Loader2 size={13} className="animate-spin" /> Catalogazione AI in corso…</>
                                    : <>Continua con {rows.length} righe <ChevronRight size={13} /></>}
                            </button>
                        )}
                        {step === "preview" && (
                            <button onClick={save} disabled={busy || !supplier.name.trim() || rows.length === 0} data-testid="bev-ai-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60">
                                {busy ? <><Loader2 size={13} className="animate-spin" /> Salvo…</> : <><Check size={13} /> Salva {rows.length} bevande</>}
                            </button>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return <div><div className="text-[10px] uppercase tracking-wider font-semibold text-brand-muted mb-1">{label}</div>{children}</div>;
}

// ============================ COCKTAILS TAB ===============================
function CocktailsTab({ pickMode = false, onPick = null }) {
    const { user } = useAuth();
    const isAdmin = !!user?.is_admin;
    const [items, setItems] = useState([]);
    const [families, setFamilies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [family, setFamily] = useState("");
    const [recipeOpen, setRecipeOpen] = useState(null);
    const [editingCocktail, setEditingCocktail] = useState(null);
    const [creatingCocktail, setCreatingCocktail] = useState(false);
    const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/cocktails/meta/families").then(({ data }) => setFamilies(data.families || [])).catch(() => {});
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set("search", search);
            if (family) params.set("family", family);
            const { data } = await api.get(`/cocktails?${params.toString()}`);
            setItems(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, family]);

    const removeCocktail = async (c) => {
        const scope = c.is_custom ? "custom" : "globale";
        if (!window.confirm(`Eliminare il cocktail "${c.name}" (${scope})?`)) return;
        try {
            await api.delete(`/cocktails/${c.id}`);
            toast.success("Cocktail eliminato");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const dedupe = async () => {
        if (!window.confirm("Eseguo dedupe del catalogo globale: elimino i cocktail con nome duplicato. Continuare?")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/cocktails/admin/dedupe");
            toast.success(`Dedupe completato · ${data.deleted} duplicati eliminati (${data.groups} gruppi)`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const resetSeed = async () => {
        if (!window.confirm("Re-seed forzato del catalogo cocktail dal file ufficiale. Continuare?")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/cocktails/admin/reset-seed");
            toast.success(`Catalogo re-seedato · ${data.catalog_count} cocktail`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div>
            <div className="flex items-center gap-2 flex-wrap mb-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca cocktail…" data-testid="ck-search" className="w-full pl-9 pr-3 py-2 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold" />
                </div>
                <select value={family} onChange={(e) => setFamily(e.target.value)} data-testid="ck-filter-family" className="px-3 py-2 rounded-lg bg-white border border-brand-line text-sm max-w-[280px]">
                    <option value="">Tutte le famiglie</option>
                    {families.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                {!pickMode && (
                    <>
                        <button
                            onClick={() => setCreatingCocktail(true)}
                            data-testid="ck-add"
                            className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-3 py-2 text-sm font-bold hover:bg-brand-navyLight transition shadow"
                        >
                            <Plus size={14} /> Nuovo cocktail
                        </button>
                        <button
                            onClick={() => setBulkUploadOpen(true)}
                            data-testid="ck-bulk-upload"
                            className="inline-flex items-center gap-1.5 rounded-full border border-brand-gold/60 bg-brand-gold/10 text-brand-navy px-3 py-2 text-sm font-bold hover:bg-brand-gold/20 transition"
                        >
                            <Upload size={14} /> Carica JSON
                        </button>
                    </>
                )}
                <div className="text-[11px] text-brand-muted ml-auto">{items.length} cocktail</div>
            </div>

            {isAdmin && !pickMode && (
                <div className="mb-3 p-3 bg-brand-navy/5 border border-brand-navy/20 rounded-xl flex items-center gap-2 flex-wrap" data-testid="ck-admin-bar">
                    <Shield size={14} className="text-brand-navy" />
                    <span className="text-xs font-bold text-brand-navy">Admin</span>
                    <button
                        onClick={dedupe}
                        disabled={busy}
                        data-testid="ck-admin-dedupe"
                        className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold bg-brand-danger/10 text-brand-danger px-3 py-1 rounded-full hover:bg-brand-danger/20 transition disabled:opacity-50"
                    >
                        <Trash2 size={11} /> Elimina duplicati catalogo
                    </button>
                    <button
                        onClick={resetSeed}
                        disabled={busy}
                        data-testid="ck-admin-reseed"
                        className="inline-flex items-center gap-1 text-[11px] font-bold bg-brand-navy/10 text-brand-navy px-3 py-1 rounded-full hover:bg-brand-navy/20 transition disabled:opacity-50"
                    >
                        <Sparkles size={11} /> Re-seed catalogo
                    </button>
                    <span className="text-[10px] text-brand-muted ml-2">Da admin puoi anche modificare/eliminare cocktail globali e fare upload bulk nel catalogo.</span>
                </div>
            )}

            {loading ? (
                <div className="py-10 text-center text-brand-muted"><Loader2 className="mx-auto animate-spin mb-2" /> Carico…</div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ck-grid">
                    {items.map((c) => {
                        const canEdit = !pickMode && (c.is_custom || isAdmin);
                        return (
                            <div key={c.id} className="bg-paper border border-brand-line rounded-xl p-4 hover:border-brand-gold/60 transition flex flex-col" data-testid={`ck-card-${c.id}`}>
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="min-w-0">
                                        <div className="font-display text-base text-brand-ink truncate">{c.name}</div>
                                        <div className="text-[11px] text-brand-muted truncate">{c.family}</div>
                                    </div>
                                    {c.is_custom
                                        ? <span className="text-[9px] uppercase tracking-wider bg-brand-gold/20 text-brand-navy px-1.5 py-0.5 rounded font-bold shrink-0">Mio</span>
                                        : isAdmin && <span className="text-[9px] uppercase tracking-wider bg-brand-navy/10 text-brand-navy px-1.5 py-0.5 rounded font-bold shrink-0">Globale</span>}
                                </div>
                                <div className="text-[11px] text-brand-muted leading-snug mb-2 line-clamp-2">{c.glass} · {c.garnish || "no garnish"}</div>
                                <div className="text-[11px] text-brand-muted mb-3">{c.ingredients?.length || 0} ingredienti · {c.abv_estimate}° vol</div>
                                <div className="mt-auto flex items-center justify-between gap-2 flex-wrap">
                                    <button onClick={() => setRecipeOpen(c)} className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition" data-testid={`ck-view-${c.id}`}>
                                        Vedi ricetta <ChevronRight size={11} className="inline" />
                                    </button>
                                    {canEdit && (
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => setEditingCocktail(c)} className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition" data-testid={`ck-edit-${c.id}`}>
                                                <Edit3 size={10} className="inline" /> Modifica
                                            </button>
                                            <button onClick={() => removeCocktail(c)} className="text-[11px] font-semibold text-brand-danger/80 hover:text-brand-danger transition" data-testid={`ck-delete-${c.id}`}>
                                                <Trash2 size={10} className="inline" /> Elimina
                                            </button>
                                        </div>
                                    )}
                                    {pickMode && (
                                        <button onClick={() => onPick(c)} className="inline-flex items-center gap-1 text-[11px] font-bold text-brand-navy bg-brand-gold/20 hover:bg-brand-gold/40 transition px-2 py-1 rounded-full" data-testid={`ck-pick-${c.id}`}>
                                            <Plus size={11} /> Aggiungi
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <RecipeDialog open={!!recipeOpen} cocktail={recipeOpen} onOpenChange={() => setRecipeOpen(null)} />
            <CocktailEditDialog
                open={!!editingCocktail || creatingCocktail}
                cocktail={editingCocktail}
                isAdmin={isAdmin}
                onOpenChange={(v) => { if (!v) { setEditingCocktail(null); setCreatingCocktail(false); } }}
                onSaved={() => { setEditingCocktail(null); setCreatingCocktail(false); load(); }}
            />
            <CocktailBulkUploadDialog
                open={bulkUploadOpen}
                isAdmin={isAdmin}
                onOpenChange={setBulkUploadOpen}
                onDone={load}
            />
        </div>
    );
}

function CocktailEditDialog({ open, cocktail, isAdmin, onOpenChange, onSaved }) {
    const isNew = !cocktail;
    const empty = {
        name: "", family: "Custom", glass: "Coppa Martini", ice: "Cubetti", garnish: "",
        instructions: "", ingredients: [{ name: "", category: "Distillato", qty_ml: 30 }], abv_estimate: 0,
    };
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (cocktail) {
            setForm({
                ...empty,
                ...cocktail,
                ingredients: cocktail.ingredients?.length ? cocktail.ingredients : empty.ingredients,
            });
        } else {
            setForm(empty);
        }
    /* eslint-disable-next-line */ }, [cocktail, open]);

    const updateIng = (idx, field, value) => {
        setForm((f) => ({
            ...f,
            ingredients: f.ingredients.map((i, j) => j === idx ? { ...i, [field]: field === "qty_ml" ? Number(value) || 0 : value } : i),
        }));
    };
    const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, { name: "", category: "Distillato", qty_ml: 30 }] }));
    const removeIng = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, j) => j !== idx) }));

    const submit = async () => {
        if (!form.name.trim()) { toast.error("Inserisci il nome del cocktail"); return; }
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                family: form.family || "Custom",
                glass: form.glass || "",
                ice: form.ice || "",
                garnish: form.garnish || "",
                instructions: form.instructions || "",
                ingredients: (form.ingredients || []).filter((i) => i.name && i.name.trim()),
                abv_estimate: Number(form.abv_estimate) || 0,
            };
            if (isNew) {
                await api.post("/cocktails", payload);
                toast.success("Cocktail creato");
            } else {
                await api.put(`/cocktails/${cocktail.id}`, payload);
                toast.success("Cocktail aggiornato");
            }
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl bg-white p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="ck-edit-dialog">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <DialogTitle className="font-display text-xl text-brand-ink">
                        {isNew ? "Nuovo cocktail" : `Modifica · ${cocktail?.name}`}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        {isNew
                            ? (isAdmin ? "Verrà creato come custom personale (per inserirlo nel catalogo globale usa l'upload JSON con target=global)." : "Verrà aggiunto ai tuoi cocktail personali.")
                            : (cocktail?.is_custom ? "Modifica del tuo cocktail custom." : "Modifica catalogo globale (solo admin).")}
                    </DialogDescription>
                </DialogHeader>
                <div className="p-6 space-y-3 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Nome *">
                            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ck-form-name" className="inp" placeholder="Es. Negroni di casa" />
                        </Field>
                        <Field label="Famiglia">
                            <input value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} className="inp" placeholder="Es. Aperitivo IBA" />
                        </Field>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Bicchiere"><input value={form.glass} onChange={(e) => setForm({ ...form, glass: e.target.value })} className="inp" /></Field>
                        <Field label="Ghiaccio"><input value={form.ice} onChange={(e) => setForm({ ...form, ice: e.target.value })} className="inp" /></Field>
                        <Field label="Garnish"><input value={form.garnish} onChange={(e) => setForm({ ...form, garnish: e.target.value })} className="inp" /></Field>
                    </div>
                    <Field label="Preparazione">
                        <textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} rows={3} className="inp" />
                    </Field>
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-brand-gold">Ingredienti</div>
                            <button onClick={addIng} className="text-[11px] font-semibold text-brand-navy hover:text-brand-gold transition inline-flex items-center gap-1">
                                <Plus size={11} /> Aggiungi
                            </button>
                        </div>
                        <div className="space-y-2">
                            {form.ingredients.map((ing, idx) => (
                                <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                                    <input value={ing.name} onChange={(e) => updateIng(idx, "name", e.target.value)} className="inp col-span-5" placeholder="Nome ingrediente" />
                                    <select value={ing.category || "Altro"} onChange={(e) => updateIng(idx, "category", e.target.value)} className="inp col-span-4">
                                        {BEV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <input type="number" step="0.5" value={ing.qty_ml} onChange={(e) => updateIng(idx, "qty_ml", e.target.value)} className="inp col-span-2" placeholder="ml" />
                                    <button onClick={() => removeIng(idx)} className="col-span-1 text-brand-danger/70 hover:text-brand-danger"><Trash2 size={13} /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                    <Field label="Gradazione finale (°)">
                        <input type="number" step="0.5" value={form.abv_estimate} onChange={(e) => setForm({ ...form, abv_estimate: e.target.value })} className="inp" />
                    </Field>
                </div>
                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2">
                    <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Annulla</button>
                    <button onClick={submit} disabled={saving} data-testid="ck-form-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60">
                        {saving ? <><Loader2 size={13} className="animate-spin" /> Salvo…</> : <><Check size={13} /> Salva</>}
                    </button>
                </div>
            </DialogContent>
            <style>{`.inp{width:100%;padding:.5rem .75rem;border-radius:.5rem;background:#fff;border:1px solid #e6dfd2;font-size:13px;outline:none}.inp:focus{border-color:#c9a961}`}</style>
        </Dialog>
    );
}

function CocktailBulkUploadDialog({ open, isAdmin, onOpenChange, onDone }) {
    const [jsonText, setJsonText] = useState("");
    const [target, setTarget] = useState(isAdmin ? "global" : "custom");
    const [busy, setBusy] = useState(false);
    useEffect(() => { setTarget(isAdmin ? "global" : "custom"); }, [isAdmin, open]);

    const submit = async () => {
        let parsed;
        try {
            parsed = JSON.parse(jsonText);
        } catch (e) {
            toast.error("JSON non valido: " + e.message);
            return;
        }
        const arr = Array.isArray(parsed) ? parsed : parsed.cocktails;
        if (!Array.isArray(arr) || arr.length === 0) {
            toast.error("Il JSON deve essere un array di cocktail o un oggetto con campo 'cocktails'");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/cocktails/bulk", { cocktails: arr, target });
            toast.success(`${data.created} cocktail creati · ${data.skipped} già esistenti (skipped)`);
            setJsonText("");
            onDone?.();
            onOpenChange(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const example = JSON.stringify([
        {
            name: "Esempio Spritz", family: "Aperitivo", glass: "Calice",
            ice: "Cubetti", garnish: "Fetta arancia",
            instructions: "Versare prosecco, aperol e soda. Mescolare brevemente.",
            ingredients: [
                { name: "Prosecco", category: "Spumante", qty_ml: 90 },
                { name: "Aperol", category: "Bitter", qty_ml: 60 },
                { name: "Soda water", category: "Mixer", qty_ml: 30 },
            ],
            abv_estimate: 10,
        },
    ], null, 2);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl bg-white p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="ck-bulk-dialog">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <DialogTitle className="font-display text-xl text-brand-ink flex items-center gap-2">
                        <Upload size={18} className="text-brand-gold" /> Carica cocktail in batch (JSON)
                    </DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">
                        Incolla un JSON con un array di cocktail. I nomi già presenti vengono saltati.
                    </DialogDescription>
                </DialogHeader>
                <div className="p-6 space-y-3 overflow-y-auto">
                    {isAdmin && (
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-brand-muted font-semibold">Target:</span>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="radio" checked={target === "global"} onChange={() => setTarget("global")} data-testid="ck-bulk-target-global" />
                                Catalogo globale (admin)
                            </label>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="radio" checked={target === "custom"} onChange={() => setTarget("custom")} data-testid="ck-bulk-target-custom" />
                                Mio custom
                            </label>
                        </div>
                    )}
                    <textarea
                        value={jsonText}
                        onChange={(e) => setJsonText(e.target.value)}
                        placeholder={example}
                        rows={14}
                        data-testid="ck-bulk-json"
                        className="w-full px-3 py-2 rounded-lg border border-brand-line bg-white font-mono text-xs"
                    />
                    <div className="text-[11px] text-brand-muted bg-brand-cream rounded-lg p-3 leading-snug">
                        Campi attesi per ogni cocktail: <code>name, family, glass, ice, garnish, instructions, ingredients[{`{name,category,qty_ml}`}], abv_estimate</code>.
                    </div>
                </div>
                <div className="px-6 py-4 bg-brand-cream border-t border-brand-line flex justify-end gap-2">
                    <button onClick={() => onOpenChange(false)} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Annulla</button>
                    <button onClick={submit} disabled={busy || !jsonText.trim()} data-testid="ck-bulk-save" className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60">
                        {busy ? <><Loader2 size={13} className="animate-spin" /> Carico…</> : <><Upload size={13} /> Carica</>}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function RecipeDialog({ open, cocktail, onOpenChange }) {
    if (!cocktail) return null;
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl bg-white p-0 overflow-hidden max-h-[90vh] flex flex-col" data-testid="ck-recipe-dialog">
                <DialogHeader className="px-6 pt-5 pb-4 border-b border-brand-line bg-gradient-to-r from-brand-gold/15 to-paper">
                    <DialogTitle className="font-display text-2xl text-brand-ink">{cocktail.name}</DialogTitle>
                    <DialogDescription className="text-sm text-brand-muted">{cocktail.family} · {cocktail.glass} · {cocktail.ice}</DialogDescription>
                </DialogHeader>
                <div className="p-6 space-y-4 overflow-y-auto">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-gold mb-2">Ingredienti</div>
                        <ul className="space-y-1.5">
                            {(cocktail.ingredients || []).map((i, idx) => (
                                <li key={idx} className="flex justify-between gap-3 text-sm">
                                    <span className="text-brand-ink"><strong>{i.qty_ml}</strong> {i.qty_ml >= 1 ? "ml" : ""} {i.name}</span>
                                    <span className="text-[11px] text-brand-muted shrink-0">{i.category}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-wider font-bold text-brand-gold mb-2">Preparazione</div>
                        <p className="text-sm text-brand-ink leading-relaxed">{cocktail.instructions}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-brand-muted">Garnish:</span> {cocktail.garnish || "—"}</div>
                        <div><span className="text-brand-muted">Gradazione finale:</span> {cocktail.abv_estimate}° vol</div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

// ========================== MAGIC BARTENDER TAB ===========================
function MagicBartenderTab() {
    const [step, setStep] = useState(1); // 1: pick, 2: event, 3: result
    const [picked, setPicked] = useState([]); // [{cocktail, count}]
    const [eventName, setEventName] = useState("Evento");
    const [people, setPeople] = useState(20);
    const [calculating, setCalculating] = useState(false);
    const [result, setResult] = useState(null);

    const addCocktail = (c) => {
        setPicked((prev) => {
            if (prev.find((x) => x.cocktail.id === c.id)) return prev;
            return [...prev, { cocktail: c, count: 10 }];
        });
        toast.success(`${c.name} aggiunto`);
    };
    const setCount = (id, count) => setPicked((prev) => prev.map((p) => p.cocktail.id === id ? { ...p, count: Math.max(1, Number(count) || 1) } : p));
    const removePick = (id) => setPicked((prev) => prev.filter((p) => p.cocktail.id !== id));

    const calculate = async () => {
        if (picked.length === 0) { toast.error("Aggiungi almeno un cocktail"); return; }
        setCalculating(true);
        try {
            const payload = {
                event_name: eventName,
                people_count: Number(people) || 1,
                cocktails: picked.map((p) => ({ cocktail_id: p.cocktail.id, count: p.count })),
                create_order_draft: true,
            };
            const { data } = await api.post("/magic-bartender/calculate", payload);
            setResult(data);
            setStep(3);
            toast.success("Calcolo completato");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCalculating(false); }
    };

    const reset = () => { setPicked([]); setResult(null); setStep(1); setEventName("Evento"); setPeople(20); };

    return (
        <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
                <StepDot n={1} active={step >= 1} label="Scegli cocktail" />
                <ChevronRight size={14} className="text-brand-muted" />
                <StepDot n={2} active={step >= 2} label="Evento & persone" />
                <ChevronRight size={14} className="text-brand-muted" />
                <StepDot n={3} active={step >= 3} label="Risultato + ordine" />
            </div>

            {step === 1 && (
                <div>
                    <div className="mb-3 p-3 bg-brand-cream/40 border border-brand-line rounded-lg flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-sm text-brand-ink"><strong>{picked.length}</strong> cocktail selezionati</div>
                        <button onClick={() => setStep(2)} disabled={picked.length === 0} data-testid="mb-next-1" className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-2 text-sm font-bold hover:bg-brand-navyLight transition disabled:opacity-50">
                            Avanti <ChevronRight size={14} />
                        </button>
                    </div>
                    {picked.length > 0 && (
                        <div className="mb-4 p-3 bg-paper border border-brand-line rounded-lg space-y-2">
                            {picked.map((p) => (
                                <div key={p.cocktail.id} className="flex items-center gap-2" data-testid={`mb-picked-${p.cocktail.id}`}>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-sm text-brand-ink truncate">{p.cocktail.name}</div>
                                        <div className="text-[11px] text-brand-muted">{p.cocktail.family}</div>
                                    </div>
                                    <input type="number" min="1" value={p.count} onChange={(e) => setCount(p.cocktail.id, e.target.value)} className="w-20 px-2 py-1 rounded border border-brand-line text-sm text-center" data-testid={`mb-count-${p.cocktail.id}`} />
                                    <button onClick={() => removePick(p.cocktail.id)} className="h-7 w-7 rounded-full text-brand-muted hover:text-brand-danger transition"><X size={14} /></button>
                                </div>
                            ))}
                        </div>
                    )}
                    <CocktailsTab pickMode onPick={addCocktail} />
                </div>
            )}

            {step === 2 && (
                <div className="max-w-lg space-y-4 p-5 bg-paper border border-brand-line rounded-xl">
                    <Field label="Nome dell'evento">
                        <input value={eventName} onChange={(e) => setEventName(e.target.value)} className="inp" data-testid="mb-event-name" />
                    </Field>
                    <Field label="Numero di persone">
                        <input type="number" min="1" value={people} onChange={(e) => setPeople(e.target.value)} className="inp" data-testid="mb-people-count" />
                    </Field>
                    <div className="p-3 bg-brand-cream/40 rounded-lg text-[12px] text-brand-muted">
                        {picked.length} cocktail · {picked.reduce((s, p) => s + p.count, 0)} drink totali
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button onClick={() => setStep(1)} className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition">Indietro</button>
                        <button onClick={calculate} disabled={calculating} data-testid="mb-calculate" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition shadow disabled:opacity-60">
                            {calculating ? <><Loader2 size={13} className="animate-spin" /> Calcolo…</> : <><Sparkles size={13} /> Calcola & genera ordine · 1 credito AI</>}
                        </button>
                    </div>
                    <style>{`.inp{width:100%;padding:.5rem .75rem;border-radius:.5rem;background:#fff;border:1px solid #e6dfd2;font-size:13px;outline:none}.inp:focus{border-color:#c9a961}`}</style>
                </div>
            )}

            {step === 3 && result && <ResultView result={result} onReset={reset} />}
        </div>
    );
}

function StepDot({ n, active, label }) {
    return (
        <div className={`inline-flex items-center gap-1.5 ${active ? "text-brand-navy" : "text-brand-muted"}`}>
            <span className={`h-5 w-5 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? "bg-brand-navy text-white" : "bg-brand-line text-brand-muted"}`}>{n}</span>
            <span className="text-[12px] font-semibold">{label}</span>
        </div>
    );
}

function ResultView({ result, onReset }) {
    const globalCount = (result.items || []).filter((it) => it.price_source === "global").length;
    const privateCount = (result.items || []).filter((it) => it.price_source === "private" || !it.price_source).length;
    return (
        <div className="space-y-4" data-testid="mb-result">
            <div className="grid grid-cols-3 gap-3">
                <Metric label="Costo totale" value={`€ ${result.total_cost_eur.toFixed(2)}`} highlight />
                <Metric label="Per persona" value={`€ ${result.cost_per_person.toFixed(2)}`} />
                <Metric label="Persone" value={result.people_count} />
            </div>
            {globalCount > 0 && (
                <div className="bg-sky-50 border border-sky-300 rounded-xl p-3 text-[12px] text-sky-900 flex items-start gap-2">
                    <Sparkles size={14} className="text-sky-700 mt-0.5 flex-shrink-0" />
                    <div>
                        <strong>{globalCount}</strong> ingredient{globalCount > 1 ? "i" : "e"} stimat{globalCount > 1 ? "i" : "o"} dal <strong>pool globale</strong> (prezzi di altri ristoranti che hanno listini pubblici).
                        {privateCount > 0 && <> · <strong>{privateCount}</strong> dal tuo listino personale.</>}
                        <span className="block text-sky-700 mt-0.5">💡 Carica il tuo listino fornitore per avere prezzi più precisi.</span>
                    </div>
                </div>
            )}
            {result.missing.length > 0 && (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
                    <div className="font-bold text-amber-900 mb-2">⚠ Ingredienti mancanti nel listino ({result.missing.length})</div>
                    <ul className="text-sm space-y-1">
                        {result.missing.map((m, i) => (
                            <li key={i} className="text-amber-900">{m.name} <span className="text-[11px] text-amber-700">({m.category}) — servono {m.qty_ml} ml</span></li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="bg-paper border border-brand-line rounded-xl p-4">
                <div className="text-[10px] uppercase tracking-wider font-bold text-brand-gold mb-2">Lista d'acquisto per fornitore</div>
                {result.by_supplier.map((s, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                        <div className="flex justify-between font-bold text-brand-ink text-sm mb-1">
                            <span>{s.supplier_name}</span>
                            <span>€ {s.total.toFixed(2)}</span>
                        </div>
                        <ul className="text-[12px] text-brand-ink space-y-0.5 pl-3">
                            {s.items.map((it, j) => (
                                <li key={j} className="flex justify-between">
                                    <span>
                                        {it.units}× {it.name} {it.brand ? `(${it.brand})` : ""}
                                        {it.price_source === "global" && (
                                            <span className="ml-1.5 inline-flex items-center text-[9px] uppercase tracking-wider font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded">stima</span>
                                        )}
                                    </span>
                                    <span className="text-brand-muted">€ {it.subtotal.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {result.order_draft_id && (
                <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 text-sm text-emerald-900">
                    ✓ Bozza ordine <code className="bg-white px-2 py-0.5 rounded">{result.order_draft_id.slice(0, 8)}</code> creata. Trovala in <strong>Ordini &amp; eventi</strong> per confermarla e inviarla.
                </div>
            )}
            <div className="flex justify-end">
                <button onClick={onReset} className="px-4 py-2 rounded-full bg-brand-navy text-white text-sm font-bold hover:bg-brand-navyLight transition" data-testid="mb-reset">Nuovo evento</button>
            </div>
        </div>
    );
}

function Metric({ label, value, highlight }) {
    return (
        <div className={`bg-paper border rounded-xl p-3 ${highlight ? "border-brand-navy" : "border-brand-line"}`}>
            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">{label}</div>
            <div className={`text-lg font-bold ${highlight ? "text-brand-navy" : "text-brand-ink"}`}>{value}</div>
        </div>
    );
}

// ============================ ORDERS TAB ===================================
function OrdersTab() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const load = async () => {
        setLoading(true);
        try { const { data } = await api.get("/beverage-orders"); setOrders(data); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const confirm = async (id) => {
        try { await api.get(`/beverage-orders/${id}/confirm`); toast.success("Ordine confermato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const remove = async (id) => {
        if (!window.confirm("Eliminare?")) return;
        try { await api.delete(`/beverage-orders/${id}`); toast.success("Eliminato"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    if (loading) return <div className="py-10 text-center text-brand-muted"><Loader2 className="mx-auto animate-spin mb-2" /> Carico…</div>;
    if (orders.length === 0) return <div className="py-10 text-center text-brand-muted text-sm">Nessun ordine ancora. Usa il Magic Bartender per generarne uno.</div>;
    return (
        <div className="space-y-3" data-testid="orders-list">
            {orders.map((o) => (
                <div key={o.id} className="bg-paper border border-brand-line rounded-xl p-4" data-testid={`order-${o.id}`}>
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                        <div>
                            <div className="font-bold text-brand-ink">{o.event_name}</div>
                            <div className="text-[11px] text-brand-muted">{o.people_count} persone · {o.suppliers?.length || 0} fornitori · {new Date(o.created_at).toLocaleString("it-IT")}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${o.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                            <div className="font-bold text-brand-navy">€ {Number(o.total_cost_eur || 0).toFixed(2)}</div>
                        </div>
                    </div>
                    <div className="text-[12px] text-brand-muted">{(o.cocktails || []).map((c) => `${c.count}× ${c.name}`).join(", ")}</div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                        {o.status !== "confirmed" && (
                            <button onClick={() => confirm(o.id)} data-testid={`order-confirm-${o.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold bg-brand-navy text-white px-3 py-1.5 rounded-full hover:bg-brand-navyLight transition">
                                <Check size={11} /> Conferma
                            </button>
                        )}
                        <button onClick={() => remove(o.id)} className="text-[11px] font-semibold text-brand-danger/80 hover:text-brand-danger transition ml-auto"><Trash2 size={11} className="inline" /> Elimina</button>
                    </div>
                </div>
            ))}
        </div>
    );
}
